import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search, X, FileText } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useQuotesList,
  formatMoney,
  type QuoteListFilters,
  type QuoteListItem,
  type QuoteStatus,
} from '@/hooks/useQuotes';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

const STATUSES: Array<QuoteStatus | 'all'> = [
  'all',
  'draft',
  'sent',
  'accepted',
  'rejected',
  'expired',
  'withdrawn',
];

export function Quotes({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<QuoteStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filters = useMemo<QuoteListFilters>(
    () => ({ status: statusFilter, search: searchTerm.trim() || undefined }),
    [statusFilter, searchTerm],
  );

  const { data, isLoading, error } = useQuotesList(activeClinicId, filters);
  const quotes = data ?? [];

  const content = (
    <>
      <div className={`${embedded ? 'mt-6' : ''} md:flex md:items-center md:justify-between`}>
        {!embedded && (
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:tracking-tight">
              {t('quotes.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('quotes.subtitle')}
            </p>
          </div>
        )}
        <div className={`flex ${embedded ? 'justify-end' : 'mt-4 md:ml-4 md:mt-0'}`}>
          <Button onClick={() => navigate('/quotes/new')} disabled={!activeClinicId}>
            <Plus className="mr-1 h-4 w-4" />
            {t('quotes.new_quote')}
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('quotes.search_placeholder')}
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm leading-5 placeholder-gray-500 focus:border-indigo-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
              aria-label={t('quotes.clear_search')}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                statusFilter === s
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {t(`quotes.status_${s}`)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mt-6">
          <ErrorAlert message={t('quotes.load_error')} />
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : quotes.length === 0 ? (
        <EmptyState hasSearch={Boolean(searchTerm)} />
      ) : (
        <div className="mt-6 overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <Th>{t('quotes.col_number')}</Th>
                  <Th>{t('quotes.col_patient')}</Th>
                  <Th>{t('quotes.col_status')}</Th>
                  <Th>{t('quotes.col_total')}</Th>
                  <Th>{t('quotes.col_valid_until')}</Th>
                  <Th>{t('quotes.col_sent_at')}</Th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">{t('quotes.col_actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                {quotes.map((q) => (
                  <tr
                    key={q.id}
                    onClick={() => navigate(`/quotes/${q.id}`)}
                    className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 font-mono text-sm text-gray-900 dark:text-white sm:pl-6">
                      {q.quote_number ?? t('quotes.no_number')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {q.patient_first_name} {q.patient_last_name}
                      </div>
                      {q.patient_pat_number && (
                        <div className="font-mono text-xs text-gray-400">
                          {q.patient_pat_number}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <QuoteStatusBadge status={q.status} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900 dark:text-white">
                      {formatMoney(q.total_cents, q.currency)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {q.valid_until
                        ? new Date(q.valid_until).toLocaleDateString('da-DK')
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {q.sent_at ? new Date(q.sent_at).toLocaleDateString('da-DK') : '—'}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/quotes/${q.id}`);
                        }}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        {t('quotes.see_details')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">{content}</div>
      </main>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-4 dark:text-white first:sm:pl-6"
    >
      {children}
    </th>
  );
}

export function QuoteStatusBadge({ status }: { status: QuoteListItem['status'] }) {
  const { t } = useTranslation();
  const cls: Record<QuoteStatus, string> = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    sent: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    accepted: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    rejected: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    expired: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    withdrawn: 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls[status]}`}
    >
      {t(`quotes.status_${status}`)}
    </span>
  );
}

function EmptyState({ hasSearch }: { hasSearch: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mt-12 text-center">
      <FileText className="mx-auto h-10 w-10 text-gray-400" />
      <h3 className="mt-4 text-base font-medium text-gray-900 dark:text-white">
        {hasSearch ? t('quotes.empty_search_title') : t('quotes.empty_title')}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {hasSearch ? t('quotes.empty_search_subtitle') : t('quotes.empty_subtitle')}
      </p>
    </div>
  );
}
